// Chapter 1: Insurance Fundamentals for Crop
// Sections 1-2

export const ch1Section1 = {
  title: "The Purpose of Crop Insurance",
  content: `
    <h2>The Purpose of Crop Insurance</h2>
    <p>Agriculture is one of the riskiest businesses in America. Unlike most industries, farmers face threats they cannot control—weather, disease, market prices, and natural disasters. A single hailstorm can destroy months of work in minutes. <strong>Crop insurance exists to provide financial stability</strong> to agricultural producers when these uncontrollable events occur.</p>
    <p>The fundamental purpose of crop insurance mirrors all insurance: <strong>transferring the financial consequences of risk</strong> from the individual (the farmer) to a larger pool (the insurance system). However, crop insurance is unique because the federal government heavily subsidizes and regulates it, recognizing that food security is a national priority.</p>
    <div class="key-concept">
      <strong>Why Crop Insurance Matters:</strong>
      <ul>
        <li><strong>Financial Protection:</strong> Shields farmers from catastrophic losses</li>
        <li><strong>Access to Credit:</strong> Lenders often require crop insurance before providing operating loans</li>
        <li><strong>Food Security:</strong> Stabilizes the agricultural sector</li>
        <li><strong>Risk Management Tool:</strong> Allows farmers to make planting decisions with financial certainty</li>
      </ul>
    </div>
    <h3>South Dakota's Agricultural Economy</h3>
    <p>South Dakota ranks among the nation's top agricultural states, producing massive quantities of corn, soybeans, wheat, and sunflowers, while ranchers raise significant cattle and hog populations.</p>
    <div class="example">
      <h4>South Dakota Example:</h4>
      <p>A Hughes County corn farmer with 2,000 acres facing 50% yield loss on $1,200/acre expected revenue would lose $1.2 million in a single season. With Revenue Protection coverage at 75%, that farmer receives an indemnity payment keeping their operation viable.</p>
    </div>
    <h3>The Insurance Mechanism</h3>
    <p>Crop insurance works through <strong>risk pooling</strong>. Many farmers pay premiums into a common pool. When individuals suffer losses, they receive payments from that pool.</p>
    <table>
      <tr><th>Component</th><th>Role</th></tr>
      <tr><td><strong>USDA Risk Management Agency (RMA)</strong></td><td>Sets policy terms, subsidizes premiums, provides reinsurance</td></tr>
      <tr><td><strong>Approved Insurance Providers (AIPs)</strong></td><td>Private companies that sell and service policies</td></tr>
      <tr><td><strong>Crop Insurance Agents</strong></td><td>Licensed producers who sell policies to farmers</td></tr>
      <tr><td><strong>Farmers/Ranchers</strong></td><td>Policyholders who purchase coverage</td></tr>
    </table>
    <div class="exam-tip">
      <strong>Exam Tip:</strong> Crop insurance is a <strong>public-private partnership</strong>. Private AIPs sell policies, but the federal government sets rules, subsidizes premiums, and provides reinsurance.
    </div>
  `
};

export const ch1Section2 = {
  title: "Risk and Related Concepts",
  content: `
    <h2>Risk and Related Concepts</h2>
    <p><strong>Risk</strong> is the uncertainty of loss—the possibility that actual results will differ from expected results.</p>
    <div class="key-concept">
      <strong>Types of Agricultural Risk:</strong>
      <ul>
        <li><strong>Production Risk:</strong> Uncertainty about crop yields due to weather, pests, disease</li>
        <li><strong>Price Risk:</strong> Uncertainty about commodity prices at harvest</li>
        <li><strong>Financial Risk:</strong> Uncertainty about ability to meet financial obligations</li>
        <li><strong>Legal/Institutional Risk:</strong> Uncertainty from regulation changes</li>
        <li><strong>Human Risk:</strong> Uncertainty from accidents, health issues, labor availability</li>
      </ul>
    </div>
    <h3>Pure Risk vs. Speculative Risk</h3>
    <p>Insurance only covers <strong>pure risk</strong>—situations with only loss or no loss possible. It does NOT cover <strong>speculative risk</strong>—situations with possibility of gain, loss, or breaking even.</p>
    <table>
      <tr><th>Pure Risk (Insurable)</th><th>Speculative Risk (NOT Insurable)</th></tr>
      <tr><td>Hail destroys wheat crop</td><td>Corn prices drop after planting</td></tr>
      <tr><td>Drought reduces soybean yield</td><td>Farmer plants new variety hoping for higher yields</td></tr>
      <tr><td>Flood prevents planting</td><td>Farmer expands acreage hoping to increase profits</td></tr>
    </table>
    <h3>Hazard vs. Peril</h3>
    <p><strong>Peril:</strong> The actual cause of loss (drought, hail, frost, insects, disease, fire, etc.)</p>
    <p><strong>Hazard:</strong> A condition increasing likelihood or severity of loss:</p>
    <table>
      <tr><th>Hazard Type</th><th>Definition</th><th>Agricultural Example</th></tr>
      <tr><td><strong>Physical Hazard</strong></td><td>Physical condition increasing loss likelihood</td><td>Low-lying field prone to flooding</td></tr>
      <tr><td><strong>Moral Hazard</strong></td><td>Dishonest tendency to cause or exaggerate loss</td><td>Farmer falsely claims prevented planting</td></tr>
      <tr><td><strong>Morale Hazard</strong></td><td>Carelessness because insurance exists</td><td>Farmer doesn't scout for pests because "insurance will cover it"</td></tr>
    </table>
    <div class="exam-tip">
      <strong>Exam Tip:</strong> Don't confuse <strong>moral</strong> hazard (intentional dishonesty) with <strong>morale</strong> hazard (carelessness). mor<u>AL</u> = <u>A</u>ctive dishonesty; mor<u>ALE</u> = <u>A</u>ttitude of carelessness.
    </div>
  `
};
